import { useMutation } from "@apollo/client/react";
import { useModal } from "@ebay/nice-modal-react";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";

import FlashContext from "components/FlashMessages/FlashContext";
import FormFieldSpace from "components/FormFields/Space";
import FormFieldTags from "components/FormFields/Tags";
import FormFieldViewText from "components/FormFields/ViewText";
import Button from "ds/components/Button";
import DrawerBody from "ds/components/Drawer/Body";
import DrawerFooter from "ds/components/Drawer/Footer";
import DrawerFooterActions from "ds/components/Drawer/FooterActions";
import DrawerHeader from "ds/components/Drawer/Header";
import DrawerCancelButton from "ds/components/DrawerNew/CancelButton";
import DrawerCloseIcon from "ds/components/DrawerNew/CloseIcon";
import DrawerForm from "ds/components/DrawerNew/Form";
import DrawerHeaderTitle from "ds/components/DrawerNew/HeaderTitle";
import { createDrawer, createDrawerTrigger } from "ds/components/DrawerNew/utils";
import FormField from "ds/components/Form/Field";
import Input from "ds/components/Input";
import Textarea from "ds/components/Textarea";
import useTypedContext from "hooks/useTypedContext";
import { getDrawerFormFix } from "utils/css";
import { containsLowercaseLettersAndNumbersOnly } from "utils/strings";

import { CREATE_PROVIDER, UPDATE_PROVIDER } from "../gql";
import {
  CreateProviderFormFields,
  CreateProviderGql,
  ProviderDrawerProps,
  UpdateProviderFormFields,
  UpdateProviderGql,
} from "./types";
import useTerraformProviderAvailableSpaces from "../useTerraformProviderAvailableSpaces";
import { TerraformProviderWithVersions } from "../types";

const ProviderDrawer = createDrawer(({ provider }: ProviderDrawerProps) => {
  const drawer = useModal();
  const isEditMode = !!provider?.id;
  const availableSpaces = useTerraformProviderAvailableSpaces(provider?.spaceDetails.id);

  const { onError, reportSuccess, reportError } = useTypedContext(FlashContext);

  const builderForm = useForm<CreateProviderFormFields>({
    defaultValues: {
      type: provider ? provider?.id : "",
      description: provider?.description || "",
      space: provider?.space || "",
      labels: provider?.labels.map((value) => ({ value })) || [],
    },
    mode: "onChange",
  });

  const {
    register,
    handleSubmit,
    formState: { errors, isDirty },
  } = builderForm;

  const [createProvider] = useMutation<CreateProviderGql>(CREATE_PROVIDER, {
    refetchQueries: ["GetProviders", "GetProviderWithVersions"],
  });

  const [updateProvider] = useMutation<UpdateProviderGql>(UPDATE_PROVIDER, {
    refetchQueries: ["GetProviders", "GetProviderWithVersions"],
  });

  const onCreateSubmit = (formData: CreateProviderFormFields) =>
    createProvider({
      variables: {
        type: formData.type,
        description: formData.description,
        space: formData.space,
        labels: formData.labels.map((item) => item.value),
      },
    })
      .then(({ data }) => {
        if (data?.terraformProviderCreate?.id) {
          reportSuccess({
            message: `Terraform provider "${data.terraformProviderCreate.id}" was successfully created`,
          });
        } else {
          reportError({
            message: "Something went wrong while creating Terraform provider, please try again.",
          });
        }

        drawer.hide();
      })
      .catch(onError);

  const onEditSubmit = (
    formData: UpdateProviderFormFields,
    provider: TerraformProviderWithVersions
  ) =>
    updateProvider({
      variables: {
        id: provider.id,
        description: formData.description,
        space: formData.space,
        labels: formData.labels.map((item) => item.value),
      },
    })
      .then(({ data }) => {
        if (data?.terraformProviderUpdate?.id) {
          reportSuccess({
            message: `Terraform provider "${data.terraformProviderUpdate.id}" was successfully saved`,
          });
        } else {
          reportError({
            message: "Something went wrong while saving Terraform provider, please try again.",
          });
        }

        drawer.hide();
      })
      .catch(onError);

  const onSubmit: SubmitHandler<CreateProviderFormFields> = async (formData) => {
    if (isEditMode) {
      await onEditSubmit(formData, provider);
    } else {
      await onCreateSubmit(formData);
    }
  };

  const typeValidator = (value: string) => {
    if (!value) return "";
    return (
      containsLowercaseLettersAndNumbersOnly(value) ||
      "Type must consist of lowercase letters and numbers only."
    );
  };

  return (
    <DrawerForm isDirty={isDirty}>
      <FormProvider {...builderForm}>
        <form onSubmit={handleSubmit(onSubmit)} {...getDrawerFormFix()}>
          <DrawerHeader justify="between">
            <DrawerHeaderTitle
              title={isEditMode ? "Edit Terraform provider" : "Create Terraform provider"}
            />
            <DrawerCloseIcon />
          </DrawerHeader>
          <DrawerBody>
            {isEditMode && <FormFieldViewText label="Type" value={provider.id} withCopy />}
            {!isEditMode && (
              <FormField
                label="Type"
                error={errors?.type?.message}
                helperText="Unique name within the account. It must consist of lowercase letters and numbers only."
              >
                {({ ariaInputProps }) => (
                  <Input
                    placeholder="Enter the unique name here..."
                    error={!!errors?.type}
                    {...register("type", {
                      required: "Type field is required.",
                      validate: typeValidator,
                    })}
                    {...ariaInputProps}
                  />
                )}
              </FormField>
            )}

            <FormFieldSpace spaces={availableSpaces} />

            <FormField label="Description" isOptional>
              {({ ariaInputProps }) => (
                <Textarea
                  placeholder="Enter description here..."
                  maxRows={15}
                  {...register("description")}
                  {...ariaInputProps}
                />
              )}
            </FormField>

            <FormFieldTags label="Labels" tagName="label" name="labels" isOptional />

            <DrawerFooter>
              <DrawerFooterActions>
                <DrawerCancelButton />
                <Button variant="primary" type="submit">
                  {isEditMode ? "Save" : "Create"}
                </Button>
              </DrawerFooterActions>
            </DrawerFooter>
          </DrawerBody>
        </form>
      </FormProvider>
    </DrawerForm>
  );
});

export const showProviderDrawer = createDrawerTrigger(ProviderDrawer);
